import { useState, useEffect } from 'react'
import { useAppStore } from '../stores/useAppStore'
import { apiEndpoints } from '../config/env'
import { apiService } from '../services/apiService'

interface InvoiceDetails {
  invoiceNumber?: string
  dateIssued?: string
  invoiceTotal?: number
  status?: string
  xeroInvoiceID?: string
  canTakePayment: boolean
}

interface InvoicePaymentProps {
  workOrderId: number
  invoiceData?: {
    invoiceNo?: string
    invoiceAmount?: number
    invoiceDate?: string
    invoiceStatus?: string
    xeroInvoiceID?: string
  }
  onPaymentSuccess?: () => void
}

const InvoicePayment = ({ workOrderId, invoiceData, onPaymentSuccess }: InvoicePaymentProps) => {
  const { addNotification } = useAppStore()
  const [invoiceDetails, setInvoiceDetails] = useState<InvoiceDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [paymentLoading, setPaymentLoading] = useState(false)

  useEffect(() => {
    if (invoiceData && invoiceData.invoiceNo) {
      // Use the invoice data from props instead of making an API call
      setInvoiceDetails({
        invoiceNumber: invoiceData.invoiceNo,
        dateIssued: invoiceData.invoiceDate,
        invoiceTotal: invoiceData.invoiceAmount,
        status: invoiceData.invoiceStatus,
        xeroInvoiceID: invoiceData.xeroInvoiceID,
        canTakePayment: invoiceData.invoiceStatus === 'AUTHORISED' || invoiceData.invoiceStatus === 'SENT'
      })
      setLoading(false)
    } else if (invoiceData && !invoiceData.invoiceNo) {
      // Invoice data is provided but no invoice exists
      setInvoiceDetails(null)
      setLoading(false)
    } else {
      // Fallback to API call if invoice data is not provided
      loadInvoiceDetails()
    }
    
    // Check if we returned from a successful payment
    if (sessionStorage.getItem('paymentSuccessCallback') === 'true') {
      sessionStorage.removeItem('paymentSuccessCallback')
      if (onPaymentSuccess) {
        onPaymentSuccess()
      }
      addNotification({
        type: 'success',
        message: 'Payment processed successfully',
        timestamp: Date.now()
      })
    }
  }, [workOrderId, invoiceData, onPaymentSuccess, addNotification])

  const loadInvoiceDetails = async () => {
    try {
      setLoading(true)
      const data = await apiService.get<InvoiceDetails>(apiEndpoints.payment.getInvoiceDetails(workOrderId))
      setInvoiceDetails(data)
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        setInvoiceDetails(null)
      } else {
        console.error('Error loading invoice details:', error)
        addNotification({
          type: 'error',
          message: 'Failed to load invoice details',
          timestamp: Date.now()
        })
      }
    } finally {
      setLoading(false)
    }
  }

  const handleTakePayment = async () => {
    if (!invoiceDetails?.invoiceNumber || !invoiceDetails?.invoiceTotal) {
      addNotification({
        type: 'error',
        message: 'Invoice details are incomplete',
        timestamp: Date.now()
      })
      return
    }

    try {
      setPaymentLoading(true)
      
      const paymentData = {
        workOrderId: workOrderId,
        invoiceNumber: invoiceDetails.invoiceNumber,
        invoiceAmount: invoiceDetails.invoiceTotal
      }

      const result = await apiService.post<{ success: boolean; checkoutUrl?: string; message?: string }>(
        apiEndpoints.payment.createStripeSession, 
        paymentData
      )

      if (result.success && result.checkoutUrl) {
        // Redirect to Stripe Checkout
        window.location.href = result.checkoutUrl
        // Note: onPaymentSuccess will be called after redirect back from Stripe
        if (onPaymentSuccess) {
          // Store callback in sessionStorage for after redirect
          sessionStorage.setItem('paymentSuccessCallback', 'true')
        }
      } else {
        throw new Error(result.message || 'Failed to create payment session')
      }
    } catch (error) {
      console.error('Error creating payment session:', error)
      addNotification({
        type: 'error',
        message: 'Failed to create payment session',
        timestamp: Date.now()
      })
    } finally {
      setPaymentLoading(false)
    }
  }

  const formatCurrency = (amount?: number) => {
    if (amount === undefined || amount === null) return ''
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount)
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return ''
    return new Date(dateString).toLocaleDateString('en-AU')
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow">
        <div className="bg-gray-500 text-white p-4 rounded-t-lg">
          <h5 className="mb-0 text-lg font-medium">Work Order Invoice</h5>
        </div>
        <div className="p-4 flex justify-center items-center min-h-[200px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <p className="ml-3">Loading invoice details...</p>
        </div>
      </div>
    )
  }

  if (!invoiceDetails) {
    return (
      <div className="bg-white rounded-lg shadow">
        <div className="bg-gray-500 text-white p-4 rounded-t-lg">
          <h5 className="mb-0 text-lg font-medium">Work Order Invoice</h5>
        </div>
        <div className="p-4">
          <div className="text-center text-gray-500 py-8">
            <div className="text-6xl mb-4">📄</div>
            <p className="text-lg font-medium mb-2">No Invoice Found</p>
            <p className="text-sm text-gray-400">No invoice has been created for this work order yet.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="bg-gray-500 text-white p-4 rounded-t-lg">
        <h5 className="mb-0 text-lg font-medium">Work Order Invoice</h5>
      </div>
      <div className="p-4">
        {/* Invoice Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Invoice No</label>
            <div className="text-gray-900 font-medium">
              {invoiceDetails.invoiceNumber || 'Not available'}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Amount</label>
            <div className="text-gray-900 font-bold text-lg text-green-600">
              {formatCurrency(invoiceDetails.invoiceTotal)}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date</label>
            <div className="text-gray-900">
              {formatDate(invoiceDetails.dateIssued)}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Status</label>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              invoiceDetails.status === 'PAID' 
                ? 'bg-green-100 text-green-800' 
                : invoiceDetails.status === 'SENT'
                ? 'bg-blue-100 text-blue-800'
                : 'bg-yellow-100 text-yellow-800'
            }`}>
              {invoiceDetails.status || 'Unknown'}
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2">
          {/* Take Payment Button */}
          {invoiceDetails.canTakePayment && (
            <button 
              onClick={handleTakePayment}
              disabled={paymentLoading}
              className="bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white font-bold py-2 px-4 rounded flex items-center"
            >
              {paymentLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processing...
                </>
              ) : (
                <>
                  💳 Take Payment
                </>
              )}
            </button>
          )}
        </div>

        {/* Payment Instructions */}
        {invoiceDetails.canTakePayment && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <div className="text-blue-400">ℹ️</div>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  Click "Take Payment" to securely process payment via Stripe. 
                  You'll be redirected to a secure payment page where the customer can enter their card details.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default InvoicePayment 